import React, { useState, useReducer, useEffect } from 'react';
import { Lock, User, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// import config
import theme from '../config/theme';
// import lib
import handleReducer, { textChange, onBlur } from '../lib/handleChange';
import isEmpty from '../lib/isEmpty';
import { getRememberData, setRememberData, removeRememberData } from '../lib/localStorage';
import toastAlert from '../lib/toastAlert';
// import validation
import { loginValid } from '../validation/auth.validation';
// import services
import { login } from '../services/auth.service';
// import components
import Input from '../custom/Input';
import ErrorSpan from '../custom/ErrorSpan';
import EyeIcon from '../custom/EyeIcon';
import ButtonLoader from '../custom/ButtonLoader';
// import Header from '../components/Header';
// import Footer from '../components/Footer';

const initialFormValue = {
    userId: '',
    password: '',
    rememberMe: false
};

const Login = () => {
    // hooks
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // state
    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [loader, setLoader] = useState(false);

    const { userId, password, rememberMe } = formValue;

    // function
    const handleChange = e => textChange(e, setFormValue);
    const handleBlur = e => onBlur(e, setTouched);
    const checkboxChange = () => {
        setTouched({ ...touched, ...{ 'rememberMe': true } })
        setFormValue({ type: 'SET', value: { ...formValue, ...{ 'rememberMe': !rememberMe } } })
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        const { status, message, errors } = await login({ userId, password }, dispatch);
        if (status === true) {
            if (rememberMe) setRememberData(JSON.stringify(formValue));
            else removeRememberData();
            toastAlert('success', message, 'login')
            navigate('/dashboard')
        } else if (errors) {
            setErrors(errors)
        } else if (message) toastAlert('error', message, 'login')
        setLoader(false);
    };

    useEffect(() => {
        const { status, errors } = loginValid(formValue);
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    useEffect(() => {
        const savedData = getRememberData();
        if (savedData) {
            const parsed = JSON.parse(savedData);
            setFormValue({ type: 'SET', value: parsed });
        }
    }, []);

    return (
        <div style={{ background: `linear-gradient(135deg, ${theme.colors.bg.primary}, ${theme.colors.bg.tertiary})` }} className="min-h-screen flex items-center justify-center p-4">
            <div style={{ background: theme.colors.bg.secondary }} className="rounded-2xl shadow-2xl w-full max-w-md p-8 border border-cyan-500/20 relative">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/')}
                    className="absolute top-6 left-6 p-2 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-lg transition-colors group"
                    title="Back to Home"
                >
                    <ArrowLeft className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300" />
                </button>
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full mb-4">
                        <Lock className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
                    <p className="text-gray-400">Gateway Promoters MLM Portal</p>
                </div>


                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">User ID</label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type="text"
                                name="userId"
                                value={userId}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                placeholder="Enter your user ID"
                            />
                        </div>
                        <ErrorSpan touched={touched} errors={errors} fieldName={'userId'} />
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                placeholder="••••••••"
                            />
                            <EyeIcon showPassword={showPassword} setShowPassword={setShowPassword} />
                        </div>
                        <ErrorSpan touched={touched} errors={errors} fieldName={'password'} />
                    </div>

                    {/* Remember & Forgot */}
                    <div className="flex items-center justify-between">
                        <label className="flex items-center">
                            <input
                                id="remember-me"
                                name="remember-me"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={checkboxChange}
                                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-400">Remember me</span>
                        </label>
                        {/* <Link to="#" className="text-sm text-cyan-400 hover:text-cyan-300 font-semibold">
                            Forgot Password?
                        </Link> */}
                    </div>

                    {/* Submit Button */}
                    <ButtonLoader
                        type="button"
                        onClick={handleSubmit}
                        loader={loader}
                        disabled={!isEmpty(errors)}
                        btnName='Sign In'
                    />
                </form>

                {/* Footer */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-400">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-cyan-400 hover:text-cyan-300 font-semibold">
                            Sign Up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;